import React, { useCallback, useEffect, useMemo, useState } from "react";
import { crmApi, extractCrmError } from "@/lib/crmApi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  MessageSquare,
  RefreshCw,
  Settings,
} from "lucide-react";

const STATE_BADGE = {
  authorized: "bg-emerald-100 text-emerald-800 border-emerald-200",
  notAuthorized: "bg-amber-100 text-amber-900 border-amber-200",
  sleepMode: "bg-slate-100 text-slate-700 border-slate-200",
  starting: "bg-blue-100 text-blue-800 border-blue-200",
};

function formatTs(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU");
}

function EnvField({ field, value, disabled, onChange }) {
  const label = field.label || field.key;
  if (field.type === "bool") {
    return (
      <div className="flex min-h-12 items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div>
          <Label className="text-sm font-medium text-slate-700">{label}</Label>
          {field.restart_required && (
            <p className="text-xs text-amber-700">Требуется перезапуск backend</p>
          )}
        </div>
        <Switch
          checked={!!value}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <Input
        type={field.type === "int" ? "number" : "text"}
        value={value ?? ""}
        disabled={disabled}
        placeholder={field.sensitive ? "Оставьте пустым, чтобы не менять" : field.default ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
      {field.sensitive && field.configured && (
        <p className="text-xs text-slate-500">Значение задано в .env (скрыто в интерфейсе)</p>
      )}
      {field.restart_required && (
        <p className="text-xs text-amber-700">Требуется перезапуск backend</p>
      )}
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-950">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export default function GreenApiSettingsPanel({ token, canEdit }) {
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (opts = {}) => {
    if (!token) return;
    const silent = opts.silent;
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const payload = await crmApi.getGreenApiSettings(token);
      setData(payload);
      const next = {};
      for (const field of payload.fields || []) {
        if (field.sensitive) {
          next[field.key] = "";
        } else {
          next[field.key] = field.value ?? "";
        }
      }
      setValues(next);
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось загрузить Green API"));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const dirtyKeys = useMemo(() => {
    if (!data?.fields) return [];
    return data.fields.filter((field) => {
      if (field.sensitive) {
        return String(values[field.key] || "").trim() !== "";
      }
      const current = field.value ?? "";
      const next = values[field.key];
      if (field.type === "bool") return Boolean(next) !== Boolean(current);
      return String(next ?? "") !== String(current ?? "");
    }).map((field) => field.key);
  }, [data, values]);

  const save = async () => {
    if (!canEdit || !dirtyKeys.length) return;
    setSaving(true);
    try {
      const payload = {};
      for (const key of dirtyKeys) {
        payload[key] = values[key];
      }
      const result = await crmApi.patchGreenApiSettings(token, { values: payload });
      if (result.restart_required) {
        toast.success("Настройки сохранены. Перезапустите backend, чтобы применить изменения.");
      } else {
        toast.success("Настройки Green API сохранены");
      }
      await load({ silent: true });
    } catch (err) {
      toast.error(extractCrmError(err, "Не удалось сохранить настройки"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаем Green API...
      </div>
    );
  }

  const state = data.state || "unknown";
  const stateBadgeClass = STATE_BADGE[state] || "bg-slate-100 text-slate-700 border-slate-200";
  const liveSync = data.live_sync;
  const stats = data.message_stats || {};

  return (
    <div className="space-y-5">
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base text-slate-950">
                <MessageSquare className="h-4 w-4 text-slate-500" />
                Состояние WhatsApp (Green API)
              </CardTitle>
              <CardDescription className="mt-1 max-w-2xl">
                Проверка авторизации инстанса и статуса live sync. Если WhatsApp разлогинился —
                откройте личный кабинет Green API и отсканируйте QR-код телефоном.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={refreshing} onClick={() => load({ silent: true })}>
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Обновить
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href={data.console_url || "https://console.green-api.com"} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Личный кабинет Green API
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 pt-5">
          <div className="flex flex-wrap items-center gap-3">
            <Badge className={stateBadgeClass}>
              {data.authorized ? (
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
              ) : (
                <AlertTriangle className="mr-1 h-3.5 w-3.5" />
              )}
              {data.state_label || state}
            </Badge>
            {data.instance?.instance_id && (
              <span className="text-sm text-slate-600">
                Инстанс: <code className="rounded bg-slate-100 px-1.5 py-0.5">{data.instance.instance_id}</code>
              </span>
            )}
            {data.instance?.base_url && (
              <span className="text-sm text-slate-500">{data.instance.base_url}</span>
            )}
          </div>

          {!data.configured && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {data.error || "Укажите ID инстанса и токен Green API в настройках ниже."}
            </div>
          )}

          {data.configured && !data.authorized && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium">Нужна повторная авторизация WhatsApp</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>Откройте личный кабинет Green API.</li>
                <li>Выберите инстанс {data.instance?.instance_id || ""}.</li>
                <li>Отсканируйте QR-код в WhatsApp → Связанные устройства.</li>
                <li>Нажмите «Обновить» на этой странице — статус должен стать «Авторизован».</li>
              </ol>
            </div>
          )}

          {data.error && data.configured && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              Ошибка запроса к Green API: {data.error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Live sync"
              value={liveSync?.running ? "работает" : liveSync ? "остановлен" : "недоступен"}
              hint={data.health?.green_api_polling_enabled ? "polling включён в .env" : "polling выключен"}
            />
            <StatCard label="Входящих в CRM" value={stats.incoming_count ?? 0} hint={`последнее: ${formatTs(stats.latest_incoming)}`} />
            <StatCard label="Исходящих в CRM" value={stats.outgoing_count ?? 0} hint={`последнее: ${formatTs(stats.latest_outgoing)}`} />
            <StatCard
              label="Отправка из CRM"
              value={data.health?.whatsapp_send_enabled ? "включена" : "выключена"}
              hint={
                data.health?.whatsapp_send_rate_limit_enabled
                  ? `лимит: ${data.health?.whatsapp_send_max_clients_per_minute ?? 5} кли./${data.health?.whatsapp_send_rate_limit_window_seconds ?? 60}с, пауза ${data.health?.whatsapp_send_min_seconds_per_client ?? 15}с`
                  : (data.health?.normalizer_enabled ? "нормализатор включён" : "нормализатор выключен")
              }
            />
          </div>

          {liveSync && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <p>
                Обработано webhook: <strong>{liveSync.processed_receipts ?? 0}</strong>,
                сохранено сообщений: <strong>{liveSync.saved_messages ?? 0}</strong>,
                пропущено: <strong>{liveSync.skipped_receipts ?? 0}</strong>
              </p>
              {liveSync.last_error_text && (
                <p className="mt-2 text-red-700">Последняя ошибка: {liveSync.last_error_text}</p>
              )}
              {liveSync.last_receipt_at && (
                <p className="mt-1 text-xs text-slate-500">Последний webhook: {formatTs(liveSync.last_receipt_at)}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {!!data.remote_settings?.length && (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-base text-slate-950">Настройки инстанса (Green API)</CardTitle>
            <CardDescription>Ключевые параметры, полученные напрямую из Green API.</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Параметр</th>
                    <th className="px-4 py-2">Значение</th>
                  </tr>
                </thead>
                <tbody>
                  {data.remote_settings.map((row) => (
                    <tr key={row.key} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-mono text-xs text-slate-600">{row.key}</td>
                      <td className="px-4 py-2 text-slate-800">{row.value == null || row.value === "" ? "—" : String(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="flex items-center gap-2 text-base text-slate-950">
            <Settings className="h-4 w-4 text-slate-500" />
            Параметры подключения
          </CardTitle>
          <CardDescription>
            Значения записываются в <code className="text-xs">{data.env_file}</code>.
            Поля с пометкой «перезапуск» вступают в силу после рестарта backend.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {(data.fields || []).map((field) => (
              <EnvField
                key={field.key}
                field={field}
                value={values[field.key]}
                disabled={!canEdit}
                onChange={(next) => setValues((prev) => ({ ...prev, [field.key]: next }))}
              />
            ))}
          </div>
          {canEdit && (
            <Button disabled={saving || !dirtyKeys.length} onClick={save}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Сохранить настройки Green API
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
